import React, { useRef, useState, ChangeEvent, DragEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ImageMarker, { Marker } from 'react-image-marker';

import { ReactComponent as CloseIcon } from '../../../../assets/icons/close-circle.svg';
import PumpIcon from '../../../../assets/icons/gas-icon.svg';
import { ReactComponent as DeviceEmptyIcon } from '../../../../assets/icons/house-searching-amico.svg';
import { ReactComponent as PlusIcon } from '../../../../assets/icons/plus.svg';
import { MarkerWithDevice } from '../../../../interface/lane-types-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../../shared-components/button/Button';
import CustomInput from '../../../../shared-components/custom-input/CustomInput';
import PageTitle from '../../../../shared-components/page-title/PageTitle';
import SelectField from '../../../../shared-components/select-field/SelectField';
import SidePaneModel from '../../../../shared-components/table-side-panel/SidePaneModel';
import { permissionCheck } from '../../../../shared-functions/JWTDocodeFunction';
import stationManagement from '../../../../utils/api/location-management';
import AddDevice from '../../../charger-pages/charger-management/components/AddDevice';

interface StationsProperties {
  onClose: () => void;
  onSaveLane: (
    newLane: { id: string; name: string; siteMapUrl: string; markers: MarkerWithDevice[] },
    rawFile: File | null,
  ) => Promise<void>;
  // eslint-disable-next-line react/require-default-props
  assignedDevices?: string[];
}

interface LaneValues {
  selectedDevice: string;
  siteMapFile: File | null;
  siteMapPreview: string | null;
}

/**
 * Converts a File object to a Base64-encoded data URL string.
 * @param {any} file - The file to convert
 * @returns {any} A promise that resolves with the Base64 data URL
 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    /**
     * onerror
     */
    reader.onload = () => resolve(reader.result as string);
    /**
     * onerror
     */
    reader.onerror = (error) => reject(error);
  });
}

/**
 * Handle drag over
 * @param {any} event - The drag event
 */
const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
  event.preventDefault();
};

/**
 * Prevent image drag
 * @param {any} event - The drag event
 */
const handlePreventDrag = (event: DragEvent<HTMLImageElement>) => {
  event.preventDefault();
};

/**
 * Add Lane Station Component
 * @param {any} param0 - The param0
 * @param {any} param0.onClose - The onClose prop
 * @param {any} param0.onSaveLane - The onSaveLane prop
 * @param {any} param0.assignedDevices - The assignedDevices prop
 */
function AddLaneLocation({ onClose, onSaveLane, assignedDevices }: StationsProperties) {
  const [laneName, setLaneName] = useState('');
  const [nameError, setNameError] = useState(false);
  const [laneValues, setLaneValues] = useState<LaneValues>({
    selectedDevice: '',
    siteMapFile: null,
    siteMapPreview: null,
  });
  const { selectedChargingPoint } = useAppSelector((state) => state.locationManagement);
  const dispatch = useAppDispatch();
  const [selectDevices, setSelectDevices] = useState(false);
  const [markers, setMarkers] = useState<MarkerWithDevice[]>([]);
  const fileInputReference = useRef<HTMLInputElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  /** All devices (you can fetch this from backend) */
  const [deviceOptions, setDeviceOptions] = useState<{ label: string; value: string }[]>([]);
  const [addDeviceOpen, setAddDeviceOpen] = useState(false);
  const { t, i18n } = useTranslation('sidePanel');

  /**
   * devices list take Api
   */

  /**
   * devices list take Api
   */
  const handleDevicesList = async () => {
    setLoading(true);
    if (!selectedChargingPoint) return;
    try {
      const response = await stationManagement.fetchDeviceList({
        chargingStation: selectedChargingPoint?.id,
      });
      const formattedDevices = response.data.map((device: any) => ({
        label: `${device.chargingPointName || ''}(${device.chargingPointSn || ''})`,
        value: device.chargingPointSn || '',
      }));

      setDeviceOptions(formattedDevices);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      dispatch(
        setNotificationMessage({
          status: true,
          message:
            error?.response?.status === 413
              ? t('invalidFileSize')
              : error?.response?.data?.message || t('somethingWentWrong'),
          type: 'error',
          code: error?.response?.status || 0,
        }),
      );
    }
  };
  /** Filter out devices already assigned to markers */
  const availableDeviceOptions = deviceOptions.filter(
    (device) =>
      !markers.some((marker) => marker.chargingPointSn === device.value) &&
      !(assignedDevices || []).includes(device.value),
  );

  /**
   * Handle device selection
   * @param {any} name - The name
   * @param {any} value - The value
   */
  const handleChangeFunction = (name: string, value: string) => {
    setLaneValues((previous) => ({ ...previous, [name]: value }));
  };

  /**
   * Handle file upload (input)
   * @param {any} event - The event
   */
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      const isVideo = file.type.startsWith('video/');
      const isSvg = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
      if (!allowedTypes.includes(file.type) || isVideo || isSvg) {
        dispatch(
          setNotificationMessage({
            status: true,
            message: t('invalidFileType'),
            type: 'error',
          }),
        );
        if (fileInputReference.current) {
          fileInputReference.current.value = '';
        }
        return;
      }
      if (file.size > 1 * 1024 * 1024) {
        dispatch(
          setNotificationMessage({
            status: true,
            message: t('invalidFileSize'),
            type: 'error',
          }),
        );
        return;
      }
      const previewUrl = URL.createObjectURL(file);
      setLaneValues({
        selectedDevice: laneValues.selectedDevice,
        siteMapFile: file,
        siteMapPreview: previewUrl,
      });
      setMarkers([]);
    }
  };

  /**
   * remove for the images and devices
   */
  const handleRemoveImage = () => {
    setLaneValues({
      selectedDevice: '',
      siteMapFile: null,
      siteMapPreview: '',
    });
    if (fileInputReference.current) {
      fileInputReference.current.value = '';
    }
  };

  /**
   * Handle drag & drop
   * @param {any} event - The event
   */
  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!event.dataTransfer.types.includes('Files')) {
      return;
    }
    const file = event.dataTransfer.files?.[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      const isVideo = file.type.startsWith('video/');
      const isSvg = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
      if (!allowedTypes.includes(file.type) || isVideo || isSvg) {
        dispatch(
          setNotificationMessage({
            status: true,
            message: t('invalidFileType'),
            type: 'error',
          }),
        );
        return;
      }
      if (file.size > 1 * 1024 * 1024) {
        dispatch(
          setNotificationMessage({
            status: true,
            message: t('invalidFileSize'),
            type: 'error',
          }),
        );
        return;
      }
      const previewUrl = URL.createObjectURL(file);
      setLaneValues({
        selectedDevice: laneValues.selectedDevice,
        siteMapFile: file,
        siteMapPreview: previewUrl,
      });
      setMarkers([]);
    }
  };

  /**
   * Trigger hidden file input
   */
  const handleUploadClick = () => {
    fileInputReference.current?.click();
  };

  /**
   * When new marker added on image
   * @param {any} marker - The marker
   */
  const handleAddMarker = (marker: Marker) => {
    if (!laneValues.selectedDevice) {
      setSelectDevices(true);
      return;
    }

    const selectedDevice = deviceOptions.find(
      (device) => device.value === laneValues.selectedDevice,
    );

    const top = Number(marker.top);
    const left = Number(marker.left);

    const markerWithDevice: MarkerWithDevice = {
      top,
      left,
      device: selectedDevice?.label || laneValues.selectedDevice,
      chargingPointSn: selectedDevice?.value || '',
      position: {
        top,
        left,
      },
    };

    setMarkers((previous) => [...previous, markerWithDevice]);
    setLaneValues((previous) => ({ ...previous, selectedDevice: '' }));
  };

  /**
   * handleRemoveMarker
   * @param {any} markerToRemove - The markerToRemove
   */
  const handleRemoveMarker = (markerToRemove: MarkerWithDevice) => {
    setMarkers((previousMarkers) =>
      previousMarkers.filter(
        (marker) =>
          marker.top !== markerToRemove.top ||
          marker.left !== markerToRemove.left ||
          marker.device !== markerToRemove.device,
      ),
    );
  };

  /**
   * Send data to backend
   */
  const handleSave = async (): Promise<void> => {
    if (!laneName.trim()) {
      setNameError(true);
      return;
    }

    if (laneValues.siteMapFile && laneValues.siteMapFile.size > 900 * 1024) {
      dispatch(
        setNotificationMessage({
          status: true,
          message: t('invalidFileSize'),
          type: 'error',
        }),
      );
      return;
    }

    setLoadingSave(true);
    try {
      let siteMapUrl = '';
      if (laneValues.siteMapFile) {
        siteMapUrl = await fileToBase64(laneValues.siteMapFile);
      }

      const newLane = {
        id: `lane-${Date.now()}`,
        name: laneName.trim(),
        siteMapUrl,
        markers,
      };

      await onSaveLane(newLane, laneValues.siteMapFile);
      onClose();
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          message:
            error?.response?.status === 413
              ? t('invalidFileSize')
              : error?.response?.data?.message || t('somethingWentWrong'),
          type: 'error',
          code: error?.response?.status || 0,
        }),
      );
    } finally {
      setLoadingSave(false);
    }
  };

  useEffect(() => {
    handleDevicesList();
  }, [selectedChargingPoint]);
  useEffect(() => {
    setTimeout(() => {
      setSelectDevices(false);
    }, 3000);
  }, [selectDevices]);
  return (
    <div className="add-lane-content" dir={i18n.dir()}>
      <div className="heading">
        <PageTitle
          title={t('addLane')}
          buttonSvgIcon={<CloseIcon />}
          buttonType="none"
          onClickButton={onClose}
          buttonLabel=" "
        />
      </div>
      <div className="lane-name-input-wrapper">
        <CustomInput
          inputLabel={t('laneNameLabel')}
          type="text"
          placeholder={t('laneNamePlaceholder')}
          value={laneName}
          name="laneName"
          onChangeValue={(name, value) => {
            setLaneName(String(value));
            setNameError(false);
          }}
          valueRequired
          error={nameError}
          errorMessage={t('laneNameRequired')}
        />

        <SelectField
          options={availableDeviceOptions}
          placeholder={t('selectDevice')}
          selectedValue={laneValues.selectedDevice}
          name="selectedDevice"
          onChangeValue={handleChangeFunction}
          error={selectDevices}
          errorMessage={t('selectDeviceError')}
        />
        <div className="add-device">
          <MyButton
            label={t('save')}
            buttonType="submit"
            onClickFunc={handleSave}
            disableButton={
              !laneName.trim() ||
              !laneValues.siteMapPreview ||
              (!permissionCheck('partnerChargingStationAdd') &&
                !permissionCheck('cpoChargingStationAdd'))
            }
            loading={loadingSave}
          />
        </div>
      </div>
      {loading && (
        <center>
          <BatteryLoader />
        </center>
      )}
      {deviceOptions.length > 0 && (
        <div
          className="main-container"
          onDrop={laneValues.siteMapPreview ? undefined : handleDrop}
          onDragOver={laneValues.siteMapPreview ? undefined : handleDragOver}>
          {laneValues.siteMapPreview ? (
            <div className="images-container">
              <div
                role="button"
                className="remove-image-button"
                onClick={handleRemoveImage}
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    handleRemoveImage();
                  }
                }}
                aria-label="Remove site map image">
                <CloseIcon />
              </div>
              <ImageMarker
                src={laneValues.siteMapPreview}
                markers={markers}
                onAddMarker={handleAddMarker}
                markerComponent={(properties: Marker) => {
                  const m = properties as MarkerWithDevice;

                  return (
                    <div className="marker-wrapper" draggable={false}>
                      <div className="device-marker" draggable={false}>
                        <img
                          src={PumpIcon}
                          alt=""
                          className="device-details-image"
                          draggable={false}
                          onDragStart={handlePreventDrag}
                        />
                        <p className="marker-device-label">{m.device}</p>
                        <div
                          role="button"
                          tabIndex={0}
                          className="remove-marker-button"
                          aria-label="Remove marker"
                          onClick={() => handleRemoveMarker(m)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') handleRemoveMarker(m);
                          }}>
                          <CloseIcon />
                        </div>
                      </div>

                      <div className="device-status-container online">
                        <div className="static-container" />
                        <div className="animation-container" />
                      </div>
                    </div>
                  );
                }}
              />
            </div>
          ) : (
            <>
              <div
                className="upload-container"
                role="button"
                tabIndex={0}
                onClick={handleUploadClick}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') handleUploadClick();
                }}>
                <p className="upload">{t('dragDrop')}</p>
              </div>

              <div className="upload-footer">
                <p>{t('uploadSiteMapHint')}</p>
                <span
                  className="upload-label"
                  role="button"
                  tabIndex={0}
                  onClick={handleUploadClick}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') handleUploadClick();
                  }}>
                  <PlusIcon /> {t('uploadSiteMap')}
                </span>

                <input
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,.jpg,.jpeg,.png"
                  ref={fileInputReference}
                  onChange={handleFileChange}
                  className="file-input"
                />
              </div>
            </>
          )}
        </div>
      )}
      {!loading && deviceOptions.length === 0 && (
        <div className="device-empty">
          <div className="icon">
            <DeviceEmptyIcon />
          </div>
          <div className="upload-footer">
            <p className="not-found-message">{t('noDevicesAdded')}</p>
            <div className="add-charger-btn-wrap">
              <MyButton
                onClickFunc={() => setAddDeviceOpen(true)}
                label={t('addCharger')}
                buttonType="primary-outline"
                svgIcon={<PlusIcon />}
                disableClass="add-device-btn-custom"
              />
            </div>
          </div>
        </div>
      )}
      <SidePaneModel
        open={addDeviceOpen}
        closePanel={() => setAddDeviceOpen(false)}
        content={<AddDevice setSidePanelOpen={setAddDeviceOpen} hideHeader />}
        position="right"
        title="addDevice"
      />
    </div>
  );
}

AddLaneLocation.defaultProps = {
  assignedDevices: [],
};

export default AddLaneLocation;
